import { useState, Fragment, useEffect, useSyncExternalStore } from "react";
import axios from "axios";
import SEED from "./es.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_LANG = "hv_lang";
const LS_MAP = "hv_i18n_es";
export const LANGS = { en: "English", es: "Español" };

const loadMap = () => {
  try { return { ...SEED, ...JSON.parse(localStorage.getItem(LS_MAP) || "{}") }; } catch { return { ...SEED }; }
};

let lang = localStorage.getItem(LS_LANG) || "en";
let map = loadMap();
let version = 0;
let langVersion = 0;
let canFetch = false;
let timer = null;
let requested = new Set();
const pending = new Set();
const listeners = new Set();

const emit = () => { version += 1; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const subscribeMap = subscribe;

// Dictionary-only lookup for auto-translated page copy: never fetches, so personal text is never sent for translation.
export const trKnown = (s) => {
  const k = s.trim();
  const hit = map[k];
  if (hit) return s.replace(k, hit);
  if (typeof window !== "undefined" && window.__hvMiss && k.length <= 600) window.__hvMiss.add(k);
  return s;
};

const flush = async () => {
  timer = null;
  const texts = [...pending];
  pending.clear();
  if (!texts.length || lang === "en") return;
  try {
    const { data } = await axios.post(`${API}/i18n/${lang}/translate`, { texts });
    const got = data?.map || {};
    if (Object.keys(got).length) {
      map = { ...map, ...got };
      try { localStorage.setItem(LS_MAP, JSON.stringify(map)); } catch { /* quota */ }
      emit();
    }
  } catch { /* stays English until the next batch */ }
};

// Translate a UI string. Unknown strings are batched to the server (cached there for everyone) and the tree re-renders when they arrive.
export const tr = (s) => {
  if (lang === "en" || typeof s !== "string") return s;
  const k = s.trim();
  if (!k || !/[A-Za-z]/.test(k)) return s;
  const hit = map[k];
  if (hit) return s.replace(k, hit);
  if (!canFetch && k.length <= 600 && typeof window !== "undefined" && window.__hvMiss) window.__hvMiss.add(k); // build-time warm-up collector
  if (canFetch && !requested.has(k) && k.length <= 600) {
    requested.add(k);
    pending.add(k);
    if (!timer) timer = setTimeout(flush, 350);
  }
  return s;
};

export const getLang = () => lang;

export const setLang = (next, { sync = true } = {}) => {
  if (!LANGS[next] || next === lang) return;
  lang = next;
  langVersion += 1;
  requested = new Set();
  localStorage.setItem(LS_LANG, next);
  document.documentElement.lang = next;
  emit();
  loadPublicMap();
  if (sync && canFetch) axios.put(`${API}/i18n/lang`, { lang: next }).catch(() => {});
};

export const setCanFetch = (v) => { canFetch = v; if (v && pending.size && !timer) timer = setTimeout(flush, 100); };

export const useLang = () => {
  useSyncExternalStore(subscribe, () => version);
  return { lang, setLang, langs: LANGS };
};

// Remounts its subtree whenever the language or the translation map changes so every tr() call re-evaluates.
let publicLoaded = "";
const loadPublicMap = async () => {
  if (lang === "en" || publicLoaded === lang) return;
  publicLoaded = lang;
  try {
    // baked dictionary ships with the site (instant), the live shared cache fills anything newer
    const [baked, live] = await Promise.allSettled([axios.get(`/i18n/${lang}.json`), axios.get(`${API}/i18n/${lang}`)]);
    const got = { ...(baked.status === "fulfilled" && typeof baked.value.data === "object" ? baked.value.data : {}), ...(live.status === "fulfilled" ? live.value.data?.map || {} : {}) };
    const fresh = Object.keys(got).filter((k) => map[k] !== got[k]);
    if (fresh.length) {
      map = { ...map, ...got };
      try { localStorage.setItem(LS_MAP, JSON.stringify(map)); } catch { /* quota */ }
    }
  } catch { /* seed only */ }
  // first dictionary load for this language: re-render once so the page doesn't stay in English until the next navigation
  readyLang = lang;
  langVersion += 1;
  emit();
};
let readyLang = "en";
export const isLangReady = () => lang === "en" || readyLang === lang;

// Remounts its subtree when the language changes (and, inside the vault, when new translations arrive) so every tr() call re-evaluates.
// Remounts its subtree only when the language changes. New translation batches update the map silently and are
// picked up on the next natural render (route change, data refresh, state change) so typed input is never lost.
export const LangProvider = ({ children, member, scope = "vault" }) => {
  useSyncExternalStore(subscribe, () => langVersion);
  useEffect(() => {
    if (scope === "vault") {
      setCanFetch(!!member);
      if (member?.lang && !localStorage.getItem(LS_LANG)) setLang(member.lang, { sync: false });
    }
    document.documentElement.lang = lang;
    loadPublicMap();
  }, [member, scope]);
  const ready = isLangReady();
  const [waited, setWaited] = useState(false);
  useEffect(() => { if (ready) return undefined; const t = setTimeout(() => setWaited(true), 1200); return () => clearTimeout(t); }, [ready]);
  if (!ready && !waited) return <div className="min-h-screen bg-background" data-testid="lang-loading" />; // brief hold instead of an English flash
  return <Fragment key={`${lang}-${langVersion}`}>{children}</Fragment>;
};
